import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { getAdminActor, updateAdminActor, getActorHistory, updateTestMode, updateAdminRole } from '../../../api/adminApi';
import { readApplicantStatus, statusMeta } from '../../../shared/applications/applicantStatus';

const EDIT_PW = process.env.REACT_APP_ADMIN_EDIT_PW;

const AVATAR_COLORS = ['#7F77DD','#D4537E','#1D9E75','#BA7517','#185FA5','#993556','#0F6E56','#993C1D'];
const getAvatarColor = id => AVATAR_COLORS[id % AVATAR_COLORS.length];

/** 어드민 전용 표시값이던 옛 필드. 이 응답에서 사라질 때까지만 읽는다. */
const LEGACY_RESULT_MAP = {
  passed:  { label: '합격',   cls: 'ag-badge--success' },
  pending: { label: '검토중', cls: 'ag-badge--warn' },
  failed:  { label: '불합격', cls: 'ag-badge--danger' },
};

/**
 * 지원 이력 한 줄의 상태 표시.
 *
 * 지원자 관리 쪽은 서버의 status로 통일됐지만, 이 이력 응답이 무엇을 싣는지는
 * 확인되지 않았다. status가 오면 그것을 쓰고, 없으면 옛 result를 읽는다.
 * 둘 다 없으면 아는 척하지 않고 '—'로 둔다.
 */
export function historyStatusBadge(app) {
  if (app?.status) return statusMeta(readApplicantStatus(app));
  const legacy = LEGACY_RESULT_MAP[app?.result];
  if (legacy) return legacy;
  return { label: '—', cls: 'ag-badge--gray' };
}

/** 권한 스위치 — 켜짐/꺼짐이 한눈에 보이도록 토글 형태 */
function PermissionToggle({ label, desc, checked, onChange, disabled, busy, danger }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>{label}</div>
        {desc && <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{desc}</div>}
      </div>
      <button
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => !disabled && !busy && onChange(!checked)}
        disabled={disabled || busy}
        style={{
          width: 44, height: 24, borderRadius: 999, border: 'none', padding: 0, flexShrink: 0,
          background: checked ? (danger ? 'var(--color-danger, #EF4444)' : '#10B981') : '#D1D5DB',
          cursor: disabled || busy ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.45 : 1,
          transition: 'background .18s',
          position: 'relative',
        }}
      >
        <span style={{
          position: 'absolute', top: 2, left: checked ? 22 : 2,
          width: 20, height: 20, borderRadius: '50%', background: '#fff',
          transition: 'left .18s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        }} />
      </button>
    </div>
  );
}

function EditAuthModal({ onConfirm, onCancel }) {
  const [pw, setPw]       = useState('');
  const [error, setError] = useState('');

  const handleConfirm = () => {
    if (pw === EDIT_PW) { onConfirm(); }
    else { setError('수정 비밀번호가 올바르지 않습니다.'); }
  };

  return (
    <div className="ag-modal-backdrop">
      <div className="ag-modal">
        <div className="ag-modal__title">수정 인증</div>
        <div className="ag-modal__sub">포트폴리오 수정을 위해 수정 비밀번호를 입력하세요.</div>
        <div className="ag-field" style={{ margin: '20px 0 0' }}>
          <input
            className="ag-field__input"
            type="password"
            placeholder="수정 비밀번호"
            value={pw}
            onChange={e => setPw(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleConfirm()}
            autoFocus
          />
        </div>
        {error && <div style={{ fontSize: 12, color: 'var(--color-danger)', marginTop: 6 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button className="ag-btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onCancel}>취소</button>
          <button className="ag-btn ag-btn--primary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleConfirm}>확인</button>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="ag-info-row">
      <span className="ag-info-row__label">{label}</span>
      <span className="ag-info-row__value">{value ?? '—'}</span>
    </div>
  );
}

export default function AdminTalentDetailPage() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [talent,       setTalent]       = useState(null);
  const [history,      setHistory]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [editMode,     setEditMode]     = useState(false);
  const [showEditAuth, setShowEditAuth] = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [roleToggling, setRoleToggling] = useState(false);
  const [permNotice,   setPermNotice]   = useState(null);
  const [form,         setForm]         = useState({});
  const [testToggling, setTestToggling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [actor, hist] = await Promise.all([
          getAdminActor(id),
          getActorHistory(id),
        ]);
        if (cancelled) return;
        setTalent(actor);
        setHistory(hist ?? []);
        setForm({
          name: actor.name, phone: actor.phone,
          email: actor.email, birthDate: actor.birthDate,
        });
      } catch {
        if (!cancelled) setError('배우 정보를 불러오지 못했습니다.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  // 권한 API는 배우 프로필 ID가 아니라 '계정(user) ID'를 받는다.
  // 없는데 talent.id로 대체하면 엉뚱한 계정의 권한을 바꾸게 되므로 아예 막는다.
  const permUserId = talent?.userId ?? null;

  const handleTestToggle = async (next) => {
    if (testToggling || permUserId == null) return;
    setTestToggling(true);
    setPermNotice(null);
    try {
      await updateTestMode(permUserId, next);
      setTalent(prev => ({ ...prev, isTest: next }));
    } catch (err) {
      setPermNotice(`테스트 계정 변경에 실패했습니다. (HTTP ${err.response?.status ?? '네트워크'})`);
    } finally {
      setTestToggling(false);
    }
  };

  const handleAdminToggle = async (next) => {
    if (roleToggling || permUserId == null) return;
    setRoleToggling(true);
    setPermNotice(null);
    try {
      await updateAdminRole(permUserId, next);
      setTalent(prev => ({ ...prev, isAdmin: next }));
    } catch (err) {
      const status = err.response?.status;
      setPermNotice(
        status === 404 || status === 405
          ? '어드민 권한 API가 아직 배포되지 않았습니다. 백엔드 배포 후 사용할 수 있습니다.'
          : `어드민 권한 변경에 실패했습니다. (HTTP ${status ?? '네트워크'})`
      );
    } finally {
      setRoleToggling(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateAdminActor(id, form);
      setTalent(prev => ({ ...prev, ...updated }));
      setEditMode(false);
    } catch {
      alert('저장에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout pageId="actors">
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
          <i className="ti ti-loader" style={{ fontSize: 24 }} /> 불러오는 중...
        </div>
      </AdminLayout>
    );
  }

  if (error || !talent) {
    return (
      <AdminLayout pageId="actors">
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-danger)' }}>
          <i className="ti ti-alert-triangle" style={{ fontSize: 24, display: 'block', marginBottom: 8 }} />
          {error || '배우 정보를 찾을 수 없습니다.'}
          <br />
          <button className="ag-btn ag-btn--sm" style={{ marginTop: 16 }} onClick={() => navigate('/admin/talents')}>
            목록으로 돌아가기
          </button>
        </div>
      </AdminLayout>
    );
  }

  const color = getAvatarColor(talent.id);

  return (
    <AdminLayout pageId="actors">
      {showEditAuth && (
        <EditAuthModal
          onConfirm={() => { setShowEditAuth(false); setEditMode(true); }}
          onCancel={() => setShowEditAuth(false)}
        />
      )}

      {/* 탑바 */}
      <div className="admin-topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="ag-btn ag-btn--sm" onClick={() => navigate('/admin/talents')}>
            <i className="ti ti-arrow-left" /> 목록
          </button>
          <h1>{talent.name} 상세</h1>
        </div>
        <div className="admin-topbar__right">
          <span className="admin-badge"><i className="ti ti-user" /> admin</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 16, alignItems: 'start' }}>

        {/* 좌측: 프로필 카드 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="ag-card" style={{ textAlign: 'center' }}>
            <div
              className="ag-avatar ag-avatar--lg"
              style={{ background: color + '22', color, margin: '0 auto 12px' }}
            >
              {talent.name.slice(0, 2)}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.03em' }}>
              {talent.name}
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 8, flexWrap: 'wrap' }}>
              <span className={`ag-badge ${talent.profilePublic ? 'ag-badge--success' : 'ag-badge--gray'}`}>
                {talent.profilePublic ? '공개' : '비공개'}
              </span>
              <span className={`ag-badge ${talent.status === 'active' ? 'ag-badge--success' : 'ag-badge--danger'}`}>
                {talent.status === 'active' ? '활성' : '정지'}
              </span>
              {talent.isTest && (
                <span className="ag-badge ag-badge--warn">테스트</span>
              )}
              {talent.isAdmin && (
                <span className="ag-badge ag-badge--danger">어드민</span>
              )}
            </div>
            {/* 계정 권한 */}
            <div style={{ marginTop: 16, textAlign: 'left', borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>계정 권한</div>

              <PermissionToggle
                label="테스트 계정"
                desc="통계·이메일 추출 등 집계에서 제외됩니다"
                checked={Boolean(talent.isTest)}
                busy={testToggling}
                disabled={permUserId == null}
                onChange={handleTestToggle}
              />

              <PermissionToggle
                label="어드민 권한"
                desc="어드민 페이지 전체에 접근할 수 있습니다"
                checked={Boolean(talent.isAdmin)}
                busy={roleToggling}
                disabled={permUserId == null}
                onChange={handleAdminToggle}
                danger
              />

              {permUserId == null && (
                <p style={{ fontSize: 12, color: 'var(--color-danger)', marginTop: 6 }}>
                  <i className="ti ti-alert-triangle" /> 계정(user) ID가 없어 권한을 변경할 수 없습니다.
                </p>
              )}
              {permNotice && (
                <p style={{ fontSize: 12, color: 'var(--color-danger)', marginTop: 6 }}>
                  <i className="ti ti-alert-triangle" /> {permNotice}
                </p>
              )}
            </div>
          </div>

          {/* 통계 */}
          <div className="ag-card">
            <div className="ag-card-title">통계</div>
            <div className="ag-info-row">
              <span className="ag-info-row__label"><i className="ti ti-eye" /> 조회수</span>
              <span className="ag-info-row__value">{talent.viewCount?.toLocaleString() ?? '—'}회</span>
            </div>
            <div className="ag-info-row">
              <span className="ag-info-row__label"><i className="ti ti-gender-male" /> 성별</span>
              <span className="ag-info-row__value">{talent.gender?.toUpperCase() === 'MALE' ? '남성' : '여성'}</span>
            </div>
            <div className="ag-info-row" style={{ borderBottom: 'none' }}>
              <span className="ag-info-row__label"><i className="ti ti-calendar" /> 출생연도</span>
              <span className="ag-info-row__value">{talent.birthDate ?? '-'}</span>
            </div>
          </div>
        </div>

        {/* 우측: 기본 정보 + 지원 이력 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* 기본 정보 */}
          <div className="ag-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div className="ag-card-title" style={{ marginBottom: 0 }}>기본 정보</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {editMode ? (
                  <>
                    <button className="ag-btn ag-btn--sm" onClick={() => setEditMode(false)} disabled={saving}>취소</button>
                    <button className="ag-btn ag-btn--primary ag-btn--sm" onClick={handleSave} disabled={saving}>
                      <i className="ti ti-check" /> {saving ? '저장 중...' : '저장'}
                    </button>
                  </>
                ) : (
                  <button className="ag-btn ag-btn--sm" onClick={() => setShowEditAuth(true)}>
                    <i className="ti ti-edit" /> 수정
                  </button>
                )}
              </div>
            </div>

            {editMode ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { label: '이름',     key: 'name' },
                  { label: '전화번호', key: 'phone' },
                  { label: '이메일',   key: 'email' },
                  { label: '생년월일', key: 'birthDate' },
                ].map(({ label, key }) => (
                  <div key={key} className="ag-field" style={{ display: 'flex', alignItems: 'center', gap: 12, margin: 0 }}>
                    <label className="ag-field__label" style={{ width: 80, flexShrink: 0, margin: 0 }}>{label}</label>
                    <input
                      className="ag-field__input"
                      value={form[key] ?? ''}
                      onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <>
                <InfoRow label="이름"     value={talent.name} />
                <InfoRow label="전화번호" value={talent.phone} />
                <InfoRow label="이메일"   value={talent.email} />
                <InfoRow label="생년월일" value={talent.birthDate ?? null} />
                <InfoRow label="성별"     value={talent.gender?.toUpperCase() === 'MALE' ? '남성' : '여성'} />
              </>
            )}
          </div>

          {/* 지원 이력 */}
          <div className="ag-card">
            <div className="ag-card-title">지원 이력</div>
            {history.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--color-text-tertiary)', fontSize: 13 }}>
                지원 이력이 없습니다
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {history.map(app => {
                  const r = historyStatusBadge(app);
                  return (
                    <div key={app.id} className="ag-apply-row">
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{app.castingTitle}</div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
                          {app.appliedAt?.slice(0, 10)}
                        </div>
                      </div>
                      <span className={`ag-badge ${r.cls}`}>{r.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
